import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, MapPin, Briefcase, Building2, CalendarClock } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Markdown } from "@/components/Markdown";
import { Button } from "@/components/ui/button";

const fmtDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

export default function JobDetail() {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/jobs/${slug}`).then((r) => setJob(r.data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="py-32 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand" /></div>;
  if (notFound || !job) return (
    <div className="py-32 text-center">
      <p className="text-brand-muted">This position could not be found.</p>
      <Link to="/jobs" className="mt-4 inline-block text-brand-terracotta underline">Back to Careers</Link>
    </div>
  );

  const mailto = `mailto:info@yayraglover.com?subject=${encodeURIComponent(`Application: ${job.title}`)}`;

  return (
    <div data-testid="job-detail-page">
      <Seo title={`${job.title} — Careers`} path={`/jobs/${job.slug}`} description={`${job.title} at Yayra Glover Organic Inputs${job.location ? " — " + job.location : ""}.`} />
      <article className="mx-auto max-w-3xl px-6 py-14">
        <Link to="/jobs" data-testid="back-to-jobs" className="inline-flex items-center gap-1.5 text-sm text-brand-muted hover:text-brand mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to Careers
        </Link>
        <h1 className="font-display text-3xl sm:text-4xl text-brand leading-tight">{job.title}</h1>
        <div className="mt-5 flex flex-wrap gap-4 text-sm text-brand-muted">
          {job.department && <span className="inline-flex items-center gap-1.5"><Building2 className="w-4 h-4" />{job.department}</span>}
          {job.location && <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" />{job.location}</span>}
          <span className="inline-flex items-center gap-1.5"><Briefcase className="w-4 h-4" />{job.employment_type}</span>
          {job.closes_at && <span className="inline-flex items-center gap-1.5"><CalendarClock className="w-4 h-4" />Closes {fmtDate(job.closes_at)}</span>}
        </div>

        <div className="mt-8 border-t border-brand/10 pt-8">
          <Markdown>{job.description}</Markdown>
        </div>

        <div className="mt-10 bg-brand-surface rounded-sm p-8 text-center">
          <h2 className="font-display text-2xl text-brand">Interested in this role?</h2>
          <p className="mt-2 text-sm text-brand-muted">Send us your CV and a short cover note.</p>
          <a href={mailto}>
            <Button data-testid="apply-btn" className="mt-5 bg-brand hover:bg-brand-hover text-white rounded-full px-6">Apply Now</Button>
          </a>
        </div>
      </article>
    </div>
  );
}
